// TEAMS Website - Button Component
// Reusable button with variants and states

import React from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/main.scss';

const Button = ({
  variant = 'primary', // primary, secondary, outline
  size = 'medium', // small, medium, large
  children,
  disabled = false,
  loading = false,
  type = 'button',
  href,
  onClick,
  className = '',
  ...props
}) => {
  const { t } = useTranslation();

  // Base classes
  const baseClasses = 'btn';
  
  // Variant classes
  const variantClasses = {
    primary: 'btn--primary',
    secondary: 'btn--secondary',
    outline: 'btn--outline'
  };

  // Size classes
  const sizeClasses = {
    small: 'btn--small',
    medium: 'btn--medium',
    large: 'btn--large'
  };

  // State classes
  const stateClasses = {
    'btn--disabled': disabled,
    'btn--loading': loading
  };

  // Combine all classes
  const buttonClasses = [
    baseClasses,
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size] || sizeClasses.medium,
    ...Object.keys(stateClasses).filter(key => stateClasses[key]),
    className
  ].filter(Boolean).join(' ');

  // Loading spinner
  const loadingSpinner = (
    <span className="btn__spinner" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="31.416"
          strokeDashoffset="31.416"
          className="btn__spinner-circle"
        />
      </svg>
    </span>
  );

  // Button content
  const buttonContent = (
    <>
      {loading && loadingSpinner}
      <span className={loading ? 'btn__text--loading' : ''}>
        {children}
      </span>
    </>
  );

  // Render as link if href is provided
  if (href) {
    return (
      <a
        href={href}
        className={buttonClasses}
        onClick={onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        {...props}
      >
        {buttonContent}
      </a>
    );
  }

  // Render as button
  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
